import { Link } from "wouter";
import type { CabinetRow } from "../lib/api.js";
import { StatusPill } from "./StatusPill.js";

function signalTone(signal: string | null) {
  if (!signal) return "muted" as const;
  const n = Number(signal);
  if (Number.isNaN(n)) return "muted" as const;
  if (n >= 19) return "ok" as const;
  if (n >= 10) return "warn" as const;
  return "bad" as const;
}

function relTime(ts: number | null) {
  if (!ts) return "never";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function utilColor(pct: number): string {
  if (pct >= 80) return "from-rose-500 to-rose-400";
  if (pct >= 50) return "from-brand-orange to-amber-400";
  return "from-brand-purple to-fuchsia-400";
}

export function CabinetCard({ cabinet }: { cabinet: CabinetRow }) {
  const slotsTotal = cabinet.totalCount ?? 0;
  const slotsUsed = (cabinet.totalCount ?? 0) - (cabinet.rentableCount ?? 0);
  const utilization = slotsTotal > 0 ? Math.round((slotsUsed / slotsTotal) * 100) : 0;

  return (
    <Link
      href={`/cabinets/${cabinet.id}`}
      className="group grid grid-cols-[auto_1fr_auto_8rem_auto] items-center gap-4 border-b border-zinc-800/60 px-4 py-3 transition hover:bg-zinc-900/40 last:border-b-0"
    >
      {/* status dot + state */}
      <div className="flex items-center gap-3">
        <span
          className={
            "h-2 w-2 rounded-full " +
            (cabinet.online
              ? "bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.7)] animate-pulse-slow"
              : "bg-rose-500")
          }
        />
        <span className="font-mono text-xs text-zinc-200 group-hover:text-zinc-50">
          {cabinet.id}
        </span>
      </div>

      {/* shop + type */}
      <div className="min-w-0 truncate text-xs text-zinc-500">
        <span className="text-zinc-400">{cabinet.shopId ?? "no shop"}</span>
        {cabinet.type && <span className="ml-2 text-zinc-600">· {cabinet.type}-slot</span>}
      </div>

      {/* signal */}
      <StatusPill tone={signalTone(cabinet.signal)}>
        {cabinet.signal ?? "—"}
      </StatusPill>

      {/* slot utilization */}
      <div className="flex items-center gap-2">
        <div className="h-1.5 w-full max-w-[80px] overflow-hidden rounded-full bg-zinc-800">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${utilColor(utilization)}`}
            style={{ width: `${utilization}%` }}
          />
        </div>
        <span className="font-mono text-[11px] text-zinc-400 tabular-nums">
          {slotsUsed}/{slotsTotal}
        </span>
      </div>

      {/* last seen + open arrow */}
      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
        <span className="tabular-nums">{relTime(cabinet.lastSeenAt)}</span>
        <span className="text-zinc-700 transition group-hover:translate-x-0.5 group-hover:text-brand-orange">
          →
        </span>
      </div>
    </Link>
  );
}
